import { Icon } from "@circle-react-shared/Icon";
import { UPGRADE_PATHS } from "@circle-react-shared/UpgradePlan/constants";
import { Typography } from "@circle-react-uikit/Typography";
import { useEffect, useEffectEvent } from "react";

import { t } from "@/i18n-js/instance";
import type { CommunityCurrentUsageStats } from "@/react/components/SettingsApp/Workflows/generated/WorkflowsApi.types";
import { useCommunityCurrentUsageStats } from "@/react/components/shared/Workflows/hooks/useCommunityCurrentUsageStats";
import type { PlanTiers } from "@/react/helpers/planHelpers";

import { useUpgradePlanContext } from "./UpgradePlanContext";
import { UpgradePlanModalBody } from "./UpgradePlanModalBody";
import { useUpgradePlanNavigate } from "./upgradePlanRouterHelpers";

const i18nRoot = "pricing_plans.upgrade_modal";

interface CheckIfPlanStatusSyncedWorkflowsAppProps {
  planTier?: PlanTiers;
}

export const CheckIfPlanStatusSyncedWorkflowsApp = ({
  planTier,
}: CheckIfPlanStatusSyncedWorkflowsAppProps) => {
  const navigate = useUpgradePlanNavigate();
  const { isEmailHubRequired } = useUpgradePlanContext();

  const checkUpgradeStatus = useEffectEvent(
    (response: CommunityCurrentUsageStats) => {
      const {
        plan_tier: responsePlanTier,
        marketing_hub_enabled: isEmailHubEnabled,
      } = response;

      const shouldCheckOnlyEmailHub = !planTier && isEmailHubRequired;
      const isPlanTierMatched = responsePlanTier === planTier;
      const isEmailHubValid = !isEmailHubRequired || isEmailHubEnabled;

      const isUpgradeSuccessful =
        (shouldCheckOnlyEmailHub && isEmailHubEnabled) ||
        (isPlanTierMatched && isEmailHubValid);

      if (isUpgradeSuccessful) {
        void navigate({ to: UPGRADE_PATHS.UPGRADE_SUCCESSFUL });
      }
    },
  );

  const { data, dataUpdatedAt, isSuccess } = useCommunityCurrentUsageStats({
    refetchInterval: 5000,
  });

  useEffect(() => {
    if (isSuccess && data) checkUpgradeStatus(data);
  }, [data, dataUpdatedAt, isSuccess]);

  return (
    <UpgradePlanModalBody>
      <div className="mt-28 text-center">
        <div className="m-auto mb-9 w-28">
          <Icon className="text-dark !h-full !w-full" type="loader" />
        </div>

        <div className="mb-28">
          <Typography.TitleLg color="text-darkest !font-bold">
            {t([i18nRoot, "loading.workflow_app_syncing"])}
          </Typography.TitleLg>
        </div>
      </div>
    </UpgradePlanModalBody>
  );
};
